import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import DOMPurify from "isomorphic-dompurify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import useWindowSize from "../../hooks/useWindowSize";
import ProgressBar from "../progress-bar/ProgressBar.component";
import moment from "moment";
import { taskCatImg } from "../../utils/taskCatImg";
import ProgressControl from "../status-control/ProgressControl.component";
import { ReactComponent as EmptyData } from "../../assets/images/global/empty-data.svg";
import { ReactComponent as SurveyIcon } from "../../assets/images/tasks/survey-icon.svg";
import TextTruncate from "react-text-truncate";
import { addPad } from "../../utils/helperFn";
import DocumentContent from "../../containers/task-dashboard/DocumentContent.container";
import { getApiUrl } from "../../utils/apiUrls";
import instance from "../../utils/axios";
import { useDispatch } from "react-redux";
import { openSnackBar } from "../../redux/actions/snackbar.actions";
import { apiErrorHandler } from "../../utils/errorHandler";

function EventTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`eventtabpanel-${index}`}
      aria-labelledby={`event-tab-${index}`}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

const EventModal = ({
  t,
  handleModal,
  handleModalChange,
  eventTimeline,
  hasActionButton,
}) => {
  
  const [searchParams] = useSearchParams();
  const getRefId = searchParams.get('refId');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const windowSize = useWindowSize();
  const [documentData, setDocumentData] = React.useState(null);
  const [state, setState] = React.useState({
    finalTaskList: [],
  });
  const modalHeadRef = React.useRef(null);
  const tabNavRef = React.useRef(null);
  const modalActionsRef = React.useRef(null);
  const [tabContentHeight, setTabContentHeight] = React.useState(null);
  const [tabValue, setTabValue] = React.useState(0);
  React.useEffect(() => {
    setTimeout(() => {
      if (
        !!modalHeadRef.current &&
        !!tabNavRef.current &&
        !!modalActionsRef.current
      ) {
        setTabContentHeight(
          window.innerHeight -
            (modalHeadRef.current.clientHeight +
              tabNavRef.current.clientHeight +
              modalActionsRef.current.clientHeight) -
            3
        );
      }
    }, 100);
  }, [handleModal]);

  React.useEffect(() => {
    if (handleModal.isOpen) {
      fetchData();
    }
  }, [handleModal]);
  const fetchData = async () => {
    try {
      const res = await instance.get(
        `${getApiUrl("getDocumentDetailsForTaskAndEvent")}?eventId=${
          handleModal?.data?.eventId
        }`
      );
      if (!!res.data) {
        setDocumentData(res.data);
      }
    } catch (error) {
      const msgObj = apiErrorHandler(error);
      dispatch(
        openSnackBar({
          msg: t(msgObj.statusText),
          type: "error",
        })
      );
    }
  };

  React.useEffect(() => {
    if(handleModal?.data) {
      setState({
        finalTaskList: !!handleModal?.data
          ? handleModal.data.candidateAllTaskList
          : [],
      });
    } else {
      setState({
        finalTaskList: [],
      });
    }
  }, [handleModal]);

  const toggleDrawer = (anchor, open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setTabValue(0);
    handleModalChange({ ...handleModal, isOpen: false });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenEvent = () => {
    navigate(`/event/${handleModal.data.eventId}${getRefId ? `?refId=${getRefId}` : ''}`, {
      state: { eventId: handleModal.data.eventId },
    });
  };

  const showTaskDate = (el) => {
    if(["NOT_CONDUCTED", "EXPIRED"].includes(el.taskStatus) && el.taskEndDate === null) {
      return;
    }

    if (["ASSESSMENT","INTERVIEW","GROUP_DISCUSSION"].includes(el.taskType)) {
      let text = "",
        taskDate = "";

      switch (el.taskStatus) {
        case "COMPLETED":
        case "EXPIRED":
        case "NOT_CONDUCTED":
        case "WAITING":
        case "IN_PROGRESS": {
          text = t("deadline");
          taskDate = moment(el.taskEndDate).format("MMM DD YYYY @ hh:mm A");
          break;
        }

        case "YET_TO_START": {
          if (!!el?.taskStartDate) {
            text = moment().isAfter(el?.taskStartDate)
              ? t("deadline")
              : t("starts_on");
            taskDate = moment().isAfter(el?.taskStartDate)
              ? moment(el?.taskEndDate).format("MMM DD YYYY @ hh:mm A")
              : moment(el.taskStartDate).format("MMM DD YYYY @ hh:mm A");
          } else {
            text = "";
            taskDate = "";
          }
          break;
        }

        default: {
          break;
        }
      }
      return text || taskDate ? (
        <span className="cm-xs-txt text-blue-gray-700 rounded-pill fw-medium">
          {text ? `${text}: ` : ""}
          <span className="text-blue-700 fw-semibold">{taskDate}</span>
        </span>
      ) : null;
    } else {
      // FOR FEEDBACK SURVEY
      return (
        <span className="cm-xs-txt text-blue-gray-700 rounded-pill fw-medium">
          {moment().isAfter(moment(el.taskStartDate))
            ? t("deadline")
            : t("starts_on")}
          :{" "}
          <span className="text-blue-700 fw-semibold">
            {moment().isAfter(moment(el.taskStartDate))
              ? moment(el.taskEndDate).format("MMM DD YYYY @ hh:mm A")
              : moment(el.taskStartDate).format("MMM DD YYYY @ hh:mm A")}
          </span>
        </span>
      );
    }
  };

  const getStatus = (el) => {
    let status = el?.taskStatus;
    if (el.taskControl === "LOCK" || el.block) {
      status = "yet_to_be_unlocked"
      return (<span className="cm-completed cm-xs-txt rounded-pill fw-medium">
        <ProgressControl value={status} />
      </span >)
    }

    if (el.taskType === "INTERVIEW" || el.taskType === "GROUP_DISCUSSION") {
      if (el?.taskStatus === "EXPIRED")
        status = !el?.taskStartDate ? "not_conducted" : "did_not_partake";
      if (el?.taskStatus === "YET_TO_START")
        status = !el?.taskStartDate ? "yet_to_be_scheduled" : "yet_to_start";
    }

    if (el.taskType === "ASSESSMENT") {
      if (el?.taskStatus === "EXPIRED") status = "did_not_partake";
      if (el?.taskStatus === "YET_TO_START") status = !el?.taskStartDate ? "yet_to_be_scheduled" : "yet_to_be_unlocked";
    }
    return status ? (
      <span className="cm-completed cm-xs-txt rounded-pill fw-medium">
        <ProgressControl value={status.toLowerCase()} />
      </span>
    ) : null;
  };

  const emptyScreen = () => {
    return <div
      className="d-flex align-items-center justify-content-center flex-column pt-4 mt-1"
      style={{
        height: !!tabContentHeight ? `${tabContentHeight}px` : "auto"
      }}
    >
      <EmptyData />
      <p className="my-0 text-blue-700 fw-semibold cm-sm-txt">
        {t("nothing_to_see")}
      </p>
    </div>
  }

  const loadTaskItem = (el, index, taskNum) => {
    var div = document.createElement("div");
    div.innerHTML = el.taskDescription;
    // div.innerHTML = testHtml;
    const trucatedText = div.innerText.substring(0, 300);
    const catName = el.taskMode === null? el.taskType : el.modeType === null ? el.taskType : el.modeType;

    return (
      <div
        className="cm-tasklist-tab-item p-3 bg-blue-gray-50 cm-btn-radius"
        key={index}
      >
        <div className="cm-tasklist-tab-item-header cm-fs-14 d-flex align-items-center mb-10">
          {catName === "FEEDBACK_SURVEY" ? <span className="text-blue-gray-700"><SurveyIcon /></span>  : <FontAwesomeIcon
            icon={taskCatImg(
              catName,
              "iconType"
            )}
            className="text-blue-gray-700"
          />}
          <p className="cm-sm-txt text-blue-800 my-0 fw-semibold">
            <span className="text-blue-gray-700">
              {t("task")} {taskNum + 1} /
            </span>{" "}
            {t(el.taskName)} {el.compType === "NOT_COMPLETED" && el.taskControl === "LOCK" && <FontAwesomeIcon icon={solid("lock")} className="text-blue-gray-500 ps-2" />}
          </p>
        </div>
        <div
          className="cm-tasklist-tab-item-content pb-2 cm-sm-txt text-blue-700"
          dangerouslySetInnerHTML={{
            __html: !!trucatedText
              ? DOMPurify.sanitize(`<p>${trucatedText}</p>`)
              : "",
          }}
        />
        <div className="cm-tasklist-tab-item-tags d-flex align-items-center">
          {getStatus(el)}
          {/* <span className='cm-xs-txt text-blue-gray-700 rounded-pill fw-medium'>{!!el.duration ? `${moment.duration(el.duration).minutes()} ${t("minutes")}` : t("deadline_based")}</span> */}
          {!(el.taskControl === "LOCK" || el.block) && <>
          {!(
            el.taskType === "INTERVIEW" ||
            el.taskType === "GROUP_DISCUSSION"
          ) ? (
            <span className="cm-xs-txt text-blue-gray-700 rounded-pill fw-medium">
              {![null, 35791394, 0].includes(el.duration)
                ? `${el.duration} ${t("mins")}`
                : t("candidate_side_deadline_based")}
            </span>
          ) : el.duration ? (
            <span className="cm-xs-txt text-blue-gray-700 rounded-pill fw-medium">
              {el.duration} {t("mins")}
            </span>
          ) : (
            ""
          )}
          {el.taskType !== "BTA" && showTaskDate(el)}
          </>}
        </div>
      </div>
    );
  }

  const renderTaskList = () => {
    let taskCount = -1;
    return state.finalTaskList.map((el, index) => {
      if(el.taskType === null && el.candidateTaskList?.length > 0) {
        return(
          <div key={el.id} className={`${index > 0 ? 'mt-3' : ''}`}>
            <p className="mb-12 text-blue-800 cm-sm-txt fw-semibold px-3 cm-truncate-1">{el.name}</p>
            {el.candidateTaskList.map((innerEl, innerIndex) => {
              taskCount = taskCount + 1;
              return loadTaskItem(innerEl, innerIndex, taskCount)
            })}
            <div className="cm-task-hr" />
          </div>
        )
      } else {
        const { name, startDate, endDate, ...otherData } = el;
        const taskCardData = {
          ...otherData,
          taskName: el.name,
          taskStartDate: el.startDate,
          taskEndDate: el.endDate
        };
        taskCount = taskCount + 1;
        return loadTaskItem(taskCardData, index, taskCount)
      }
    })
  }

  const list = (anchor) => (
    <Box
      sx={{ width: windowSize.screenWidth <= 991 ? "100%" : 800 }}
      role="presentation"
      // onClick={toggleDrawer(anchor, false)}
      onKeyDown={toggleDrawer(anchor, false)}
    >
      {!!handleModal.data ? (
        <>
          <div
            className="cm-tasklist-modal-header position-relative py-3 px-4"
            ref={modalHeadRef}
          >
            <p className="text-blue-800 my-0 pb-2 cm-fs-14 fw-medium">
              <TextTruncate
                line={2}
                element="span"
                truncateText="…"
                text={handleModal.data.eventName}
              />
            </p>
            <div className="cm-av-event-tags d-flex flex-wrap align-items-center">
              <span className="cm-av-event-tag cm-sm-txt text-blue-gray-700 rounded-pill cm-fs-12 fw-medium">
                {handleModal.data?.count?.completedTabCount + handleModal?.data.count?.notCompletedTabCount + " "}
                {(handleModal.data?.count?.completedTabCount + handleModal?.data.count?.notCompletedTabCount) > 1 ? t("tasks") : t("task")}
              </span>
              <span
                className={`cm-av-event-tag cm-sm-txt text-blue-700 rounded-pill ms-1 cm-fs-12 ${
                  windowSize.isMobile ? "fw-semibold" : "fw-medium"
                }`}
              >
                {!windowSize.isMobile ? (
                  <span className="text-blue-gray-700">
                    {t("event_timeline")}:
                  </span>
                ) : (
                  ""
                )}{" "}
                {!!eventTimeline && eventTimeline}
              </span>
            </div>
            <FontAwesomeIcon
              icon={regular("times")}
              className="text-blue-gray-700 cm-tasklist-modal-close cm-pointer position-absolute"
              onClick={toggleDrawer("right", false)}
            />
          </div>
          <div className="cm-tasklist-modal-body">
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              className="cm-tasklist-tab-nav px-0"
              ref={tabNavRef}
            >
              <Tab
                label={t("instructions")}
                disableRipple
                className={`cm-tasklist-tab fw-medium cm-fs-14 ${
                  windowSize.isMobile ? "text-capitalize" : ""
                }`}
              />
              <Tab
                label={t("task_list")}
                icon={
                  <span className="cm-events-num text-white rounded-pill fw-medium cm-xs-txt cm-fs-12">
                    {addPad(handleModal.data.count?.completedTabCount + handleModal.data.count?.notCompletedTabCount)}
                  </span>
                }
                iconPosition="end"
                disableRipple
                className={`cm-tasklist-tab fw-medium ${
                  windowSize.isMobile ? "text-capitalize" : ""
                }`}
              />
              <Tab
                label={t("document")}
                icon={
                  <span className="cm-events-num text-white rounded-pill fw-medium cm-xs-txt cm-fs-12">
                    {documentData?.length > 0 ?addPad(documentData?.length):addPad(0)}
                  </span>
                }
                iconPosition="end"
                disableRipple
                className={`cm-tasklist-tab fw-medium ${
                  windowSize.isMobile ? "text-capitalize" : ""
                }`}
              />
            </Tabs>
            <EventTabPanel
              value={tabValue}
              index={0}
              className="cm-sm-txt text-primary cm-tasklist-tab-content position-relative"
              style={{
                height: !!tabContentHeight ? `${tabContentHeight}px` : "auto",
                overflow: "auto",
              }}
            >
              {!!handleModal.data.eventNotes ? (
                <div
                  className="text-blue-700"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(handleModal.data.eventNotes),
                  }}
                />
              ) : (
                emptyScreen()
              )}
            </EventTabPanel>
            <EventTabPanel
              value={tabValue}
              index={1}
              className="cm-sm-txt text-primary cm-tasklist-tab-content position-relative"
              style={{
                height: !!tabContentHeight ? `${tabContentHeight}px` : "auto",
                overflow: "auto",
              }}
            >
              {state.finalTaskList?.length > 0 ? (renderTaskList()) : (
                emptyScreen()
              )}
            </EventTabPanel>
            <EventTabPanel
              value={tabValue}
              index={2}
              className="cm-tasklist-tab-content"
              style={{
                height: !!tabContentHeight ? `${tabContentHeight}px` : "auto",
                overflow: "auto",
              }}
            >
              <DocumentContent
                t={t}
                eventId={handleModal?.data?.eventId}
                documentData={documentData}
                contentHeight={tabContentHeight}
              />
            </EventTabPanel>
            <div
              className="cm-modal-actions-wrap d-flex cm-tasklist-tab-content"
              ref={modalActionsRef}
            >
              {!!hasActionButton && (
                <span
                  onClick={handleOpenEvent}
                  className="btn btn-primary cm-action-btn cm-action-hover me-3 bg-action-blue text-white cm-btn cm-sm-txt cm-btn-resp"
                >
                  {t("open_event")}
                </span>
              )}
              <button
                className="btn btn-primary bg-white cm-trans-btn cm-trans-btn-inner cm-btn text-action-blue cm-sm-txt cm-light-hover cm-btn-resp"
                onClick={toggleDrawer("right", false)}
              >
                {t("close")}
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="d-flex py-4 align-items-center justify-content-center">
          <ProgressBar />
        </div>
      )}
    </Box>
  );

  return (
    <div>
     <React.Fragment>
        <Drawer
          anchor={"right"}
          open={handleModal.isOpen}
          onClose={toggleDrawer("right", false)}
          className={`${
            windowSize.isMobile
              ? "cm-tasklist-modal-wrapper-mob-fv"
              : "cm-tasklist-modal-wrapper"
          }`}
        >
          {list("right")}
        </Drawer>
      </React.Fragment>
    </div>
  );
};

export default EventModal;
